import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import LinearProgress from '@material-ui/core/LinearProgress';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import TrendingFlatIcon from '@material-ui/icons/TrendingFlat';
import { YearlyComparisonSkeleton } from './SkeletonComponents';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    height: '100%',
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  comparisonItem: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[50],
  },
  itemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
  },
  itemTitle: {
    fontWeight: 500,
  },
  trendIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  trendUp: {
    color: '#4caf50',
    fontWeight: 'bold',
  },
  trendDown: {
    color: '#f44336',
    fontWeight: 'bold',
  },
  trendStable: {
    color: '#757575',
    fontWeight: 'bold',
  },
  progressContainer: {
    marginTop: theme.spacing(1),
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
  },
  values: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(0.5),
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
}));

const YearlyComparisonCard = ({ metrics }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  if (!metrics) return <YearlyComparisonSkeleton />;

  const comparisons = [
    {
      key: 'incidences',
      title: t('tables.incidence.name'),
      data: metrics.incidencesYearly,
      color: '#ff7043',
    },
    {
      key: 'assistance',
      title: t('tables.assistance.name'),
      data: metrics.assistanceYearly,
      color: '#9ccc65',
    },
    {
      key: 'orders',
      title: t('tables.orders.name'),
      data: metrics.ordersYearly,
      color: '#42a5f5',
    },
    {
      key: 'revenue',
      title: t('dashboard.revenue'),
      data: metrics.revenueYearly,
      color: '#4caf50',
      isCurrency: true,
    },
  ];

  const formatValue = (value, isCurrency = false) => {
    if (isCurrency) {
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    }
    return value.toString();
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon className={classes.trendUp} fontSize="small" />;
      case 'down':
        return <TrendingDownIcon className={classes.trendDown} fontSize="small" />;
      default:
        return <TrendingFlatIcon className={classes.trendStable} fontSize="small" />;
    }
  };

  const getTrendClass = (trend) => {
    switch (trend) {
      case 'up':
        return classes.trendUp;
      case 'down':
        return classes.trendDown;
      default:
        return classes.trendStable;
    }
  };

  return (
    <Paper className={classes.paper}>
      <Typography variant="h6" className={classes.title}>
        {t('dashboard.yearly_comparison')}
      </Typography>

      {comparisons.map((item) => {
        const data = item.data || {};
        const max = Math.max(data.current || 0, data.sameMonthLastYear || 0) || 1;
        const progress = ((data.current || 0) / max) * 100;
        const changePercent = data.changePercent || 0;

        return (
          <Box key={item.key} className={classes.comparisonItem}>
            <Box className={classes.itemHeader}>
              <Typography variant="body2" className={classes.itemTitle}>
                {item.title}
              </Typography>
              <Box className={classes.trendIndicator}>
                {getTrendIcon(data.trend)}
                <Typography
                  variant="body2"
                  className={getTrendClass(data.trend)}
                >
                  {changePercent >= 0 ? '+' : ''}
                  {changePercent}%
                </Typography>
              </Box>
            </Box>

            <Box className={classes.progressContainer}>
              <LinearProgress
                variant="determinate"
                value={progress}
                className={classes.progressBar}
                style={{
                  backgroundColor: `${item.color}30`,
                }}
                classes={{
                  bar: {
                    backgroundColor: item.color,
                  },
                }}
              />
              <Box className={classes.values}>
                <span>
                  {t('dashboard.this_year')}: {formatValue(data.current || 0, item.isCurrency)}
                </span>
                <span>
                  {t('dashboard.same_month_last_year')}: {formatValue(data.sameMonthLastYear || 0, item.isCurrency)}
                </span>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Paper>
  );
};

export default YearlyComparisonCard;
